import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Plus, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useProjectGroupMeetingNotes,
  useCreateMeetingNote,
} from "@/hooks/useMeetingNotes";
import { MeetingNoteCard } from "../project-detail/meeting-notes/MeetingNoteCard";

interface ProjectGroupMeetingNotesSectionProps {
  projectGroupId: string;
}

export function ProjectGroupMeetingNotesSection({
  projectGroupId,
}: ProjectGroupMeetingNotesSectionProps) {
  const navigate = useNavigate();
  const { data: notes = [], isLoading } =
    useProjectGroupMeetingNotes(projectGroupId);
  const createMeetingNote = useCreateMeetingNote();

  const handleCreateNote = () => {
    createMeetingNote.mutate(
      {
        title: "Untitled Meeting Note",
        meeting_date: format(new Date(), "yyyy-MM-dd"),
        project_group_id: projectGroupId,
      },
      {
        onSuccess: (note) => {
          navigate(`/meeting-notes/${note.id}?edit=1`);
        },
      },
    );
  };

  const handleJumpToNote = (noteId: string) => {
    const element = document.getElementById(`meeting-note-${noteId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const sortedNotes = useMemo(() => {
    return [...notes].sort(
      (a, b) =>
        new Date(b.meeting_date).getTime() - new Date(a.meeting_date).getTime(),
    );
  }, [notes]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">Meeting Notes</h2>
          {sortedNotes.length > 0 && (
            <Select onValueChange={handleJumpToNote}>
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="Jump to note..." />
              </SelectTrigger>
              <SelectContent>
                {sortedNotes.map((note) => (
                  <SelectItem key={note.id} value={note.id}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(note.meeting_date), "MMM d")}
                      </span>
                      <span className="truncate max-w-[150px]">
                        {note.title}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <Button
          onClick={handleCreateNote}
          disabled={createMeetingNote.isPending}
          data-testid="create-project-group-meeting-note-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          {createMeetingNote.isPending ? "Creating..." : "New Meeting Note"}
        </Button>
      </div>

      {/* Notes list */}
      {sortedNotes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <CardTitle className="text-lg mb-1">No meeting notes yet</CardTitle>
            <p className="text-muted-foreground text-sm max-w-sm">
              Create your first meeting note to track discussions and decisions
              for this project group.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedNotes.map((note) => (
            <MeetingNoteCard key={note.id} note={note} collapsible />
          ))}
        </div>
      )}
    </div>
  );
}
